import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';

const today = new Date().toISOString().split('T')[0];

const emptyAsset = {
  type: '',
  name: '',
  category: '',
  description: '',
  serialNumber: '',
  purchaseDate: '',
  purchaseAmount: '',
  currentValue: '',
  location: '',
  condition: 'good',
  status: 'active',
  notes: '',
  licenseDetails: {
    issueDate: '',
    expirationDate: '',
    renewalFee: '',
    issuingAuthority: '',
    licenseNumber: ''
  },
  documentDetails: {
    documentNumber: '',
    issuingAuthority: '',
    validFrom: '',
    validTo: ''
  }
};

const emptyMaintenance = {
  date: today,
  description: '',
  cost: '',
  performedBy: '',
  nextMaintenanceDate: ''
};

const assetTypeLabels = {
  license: 'License',
  property: 'Property',
  equipment: 'Equipment',
  document: 'Document'
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleDateString();
};

const toInputDate = (value) => {
  if (!value) return '';
  return new Date(value).toISOString().split('T')[0];
};

const cleanPayload = (data) => {
  const cleanObject = (value) => {
    if (!value || typeof value !== 'object') return value;
    const result = {};
    Object.entries(value).forEach(([key, childValue]) => {
      if (typeof childValue === 'string') {
        const trimmed = childValue.trim();
        if (trimmed) result[key] = trimmed;
      } else if (childValue !== undefined && childValue !== null && childValue !== '') {
        result[key] = childValue;
      }
    });
    return result;
  };

  const payload = {};
  Object.entries(data).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !(value instanceof Date)) {
      const nested = cleanObject(value);
      if (Object.keys(nested).length) payload[key] = nested;
    } else if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) payload[key] = trimmed;
    } else if (value !== undefined && value !== null && value !== '') {
      payload[key] = value;
    }
  });
  return payload;
};

const csvEscape = (value) => {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const actorLabel = (person) => {
  if (!person?.name) return 'N/A';
  return person.employeeId ? `${person.name} (${person.employeeId})` : person.name;
};

const ModalShell = ({ title, children, onClose, wide = false }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className={`relative top-8 mx-auto p-5 border w-11/12 ${wide ? 'max-w-5xl' : 'md:w-3/4 lg:w-1/2'} shadow-lg rounded-md bg-white`}>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
          &times;
        </button>
      </div>
      {children}
    </div>
  </div>
);

const StatCard = ({ label, value, tone = 'text-gray-900' }) => (
  <div className="card p-4">
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <p className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</p>
  </div>
);

const Assets = () => {
  const { canAccess, hasRole } = useAuth();
  const [assets, setAssets] = useState([]);
  const [expiringLicenses, setExpiringLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', type: '', status: '' });
  const [modalType, setModalType] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetType, setAssetType] = useState('');

  const canManageAssets = hasRole(['admin', 'accounts']);
  const canArchiveAssets = hasRole('admin');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({ defaultValues: emptyAsset });

  const {
    register: registerMaintenance,
    handleSubmit: handleMaintenanceSubmit,
    reset: resetMaintenance,
    formState: { errors: maintenanceErrors, isSubmitting: isSavingMaintenance }
  } = useForm({ defaultValues: emptyMaintenance });

  const watchedType = watch('type');

  useEffect(() => {
    if (watchedType !== undefined) setAssetType(watchedType);
  }, [watchedType]);

  const fetchAssets = useCallback(async () => {
    if (!canAccess('assets')) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '100' });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/api/assets?${params.toString()}`);
      setAssets(response.data.assets || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  }, [canAccess, filters]);

  const fetchExpiringLicenses = useCallback(async () => {
    if (!canAccess('assets')) return;

    try {
      const response = await api.get('/api/assets/expiring-licenses?days=30');
      setExpiringLicenses(response.data.expiringLicenses || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch expiring licenses');
    }
  }, [canAccess]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  useEffect(() => {
    fetchExpiringLicenses();
  }, [fetchExpiringLicenses]);

  const stats = useMemo(() => {
    return assets.reduce(
      (acc, asset) => {
        acc.total += 1;
        if (asset.status === 'active') acc.active += 1;
        if (asset.status === 'under_maintenance') acc.maintenance += 1;
        acc.value += Number(asset.currentValue || asset.purchaseAmount || 0);
        return acc;
      },
      { total: 0, active: 0, maintenance: 0, value: 0 }
    );
  }, [assets]);

  const resetFilters = () => {
    setFilters({ search: '', type: '', status: '' });
  };

  const assetToForm = (asset) => ({
    type: asset.type || '',
    name: asset.name || '',
    category: asset.category || '',
    description: asset.description || '',
    serialNumber: asset.serialNumber || '',
    purchaseDate: toInputDate(asset.purchaseDate),
    purchaseAmount: asset.purchaseAmount ?? '',
    currentValue: asset.currentValue ?? '',
    location: asset.location || '',
    condition: asset.condition || 'good',
    status: asset.status || 'active',
    notes: asset.notes || '',
    licenseDetails: {
      issueDate: toInputDate(asset.licenseDetails?.issueDate),
      expirationDate: toInputDate(asset.licenseDetails?.expirationDate),
      renewalFee: asset.licenseDetails?.renewalFee ?? '',
      issuingAuthority: asset.licenseDetails?.issuingAuthority || '',
      licenseNumber: asset.licenseDetails?.licenseNumber || ''
    },
    documentDetails: {
      documentNumber: asset.documentDetails?.documentNumber || '',
      issuingAuthority: asset.documentDetails?.issuingAuthority || '',
      validFrom: toInputDate(asset.documentDetails?.validFrom),
      validTo: toInputDate(asset.documentDetails?.validTo)
    }
  });

  const openModal = async (type, asset = null) => {
    setModalType(type);
    setSelectedAsset(asset);

    if (type === 'create') {
      reset(emptyAsset);
      setAssetType('');
      return;
    }

    if (type === 'edit' && asset) {
      reset(assetToForm(asset));
      setAssetType(asset.type || '');
      return;
    }

    if (type === 'maintenance') {
      resetMaintenance(emptyMaintenance);
    }
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedAsset(null);
  };

  const onSubmit = async (data) => {
    const payload = cleanPayload(data);

    try {
      if (modalType === 'create') {
        await api.post('/api/assets', payload);
        toast.success('Asset created');
      } else {
        await api.put(`/api/assets/${selectedAsset._id}`, payload);
        toast.success('Asset updated');
      }

      closeModal();
      fetchAssets();
      fetchExpiringLicenses();
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to save asset');
    }
  };

  const onMaintenanceSubmit = async (data) => {
    try {
      await api.post(`/api/assets/${selectedAsset._id}/maintenance`, cleanPayload(data));
      toast.success('Maintenance record added');
      closeModal();
      fetchAssets();
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to add maintenance record');
    }
  };

  const archiveAsset = async (asset) => {
    if (!window.confirm(`Archive ${asset.name}?`)) return;

    try {
      await api.delete(`/api/assets/${asset._id}`);
      toast.success('Asset archived');
      fetchAssets();
      fetchExpiringLicenses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to archive asset');
    }
  };

  const exportAssetCsv = (asset) => {
    if (!asset) return;

    const rows = [
      ['Asset', 'Type', 'Status', 'Category', 'Location', 'Current Value'],
      [
        asset.name,
        assetTypeLabels[asset.type] || asset.type,
        asset.status,
        asset.category,
        asset.location,
        Number(asset.currentValue || asset.purchaseAmount || 0)
      ],
      [],
      ['Maintenance Date', 'Description', 'Cost', 'Performed By', 'Entered By', 'Next Maintenance Date'],
      ...(asset.maintenanceHistory || []).map((record) => [
        formatDate(record.date),
        record.description,
        Number(record.cost || 0),
        record.performedBy,
        actorLabel(record.addedBy),
        formatDate(record.nextMaintenanceDate)
      ])
    ];

    const csv = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `asset-${asset.name}-statement.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Asset statement exported');
  };

  const printAssetStatement = () => {
    window.print();
  };

  const statusBadge = (status) => {
    switch (status) {
      case 'active':
        return 'status-approved';
      case 'under_maintenance':
        return 'status-pending';
      case 'disposed':
      case 'lost':
        return 'status-rejected';
      default:
        return 'bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium';
    }
  };

  const assetExpiry = (asset) => {
    if (asset.type === 'license') return asset.licenseDetails?.expirationDate;
    if (asset.type === 'document') return asset.documentDetails?.validTo;
    return null;
  };

  if (!canAccess('assets')) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Assets</h1>
          <p className="text-gray-600">Track licenses, documents, equipment, property, and maintenance</p>
        </div>
        {canManageAssets && (
          <button onClick={() => openModal('create')} className="btn-primary flex items-center justify-center">
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Asset
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Assets" value={stats.total} />
        <StatCard label="Active" value={stats.active} tone="text-green-600" />
        <StatCard label="Expiring Licenses" value={expiringLicenses.length} tone="text-yellow-600" />
        <StatCard label="Tracked Value" value={formatCurrency(stats.value)} tone="text-blue-600" />
      </div>

      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2 relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              className="input-field !pl-11"
              placeholder="Search name, category, description"
            />
          </div>
          <select
            value={filters.type}
            onChange={(event) => setFilters((prev) => ({ ...prev, type: event.target.value }))}
            className="input-field"
          >
            <option value="">All types</option>
            <option value="license">License</option>
            <option value="property">Property</option>
            <option value="equipment">Equipment</option>
            <option value="document">Document</option>
          </select>
          <div className="flex gap-2">
            <select
              value={filters.status}
              onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
              className="input-field"
            >
              <option value="">All status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="under_maintenance">Under Maintenance</option>
              <option value="disposed">Disposed</option>
              <option value="lost">Lost</option>
            </select>
            <button onClick={resetFilters} className="btn-secondary flex items-center justify-center" title="Reset filters">
              <ArrowPathIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {expiringLicenses.length > 0 && (
        <div className="card p-4 border-yellow-200 bg-yellow-50">
          <h2 className="text-sm font-semibold text-yellow-900">Licenses expiring within 30 days</h2>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            {expiringLicenses.slice(0, 6).map((asset) => (
              <div key={asset._id} className="bg-white border border-yellow-200 rounded-md p-3">
                <p className="font-medium text-gray-900">{asset.name}</p>
                <p className="text-sm text-yellow-700">{formatDate(asset.licenseDetails?.expirationDate)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asset</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry / Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center">
                    <div className="spinner mx-auto"></div>
                  </td>
                </tr>
              ) : assets.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">No assets found</td>
                </tr>
              ) : (
                assets.map((asset) => (
                  <tr key={asset._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{asset.name}</div>
                      <div className="text-sm text-gray-500">{asset.category || 'No category'}</div>
                      {asset.serialNumber && <div className="text-xs text-gray-400">Serial: {asset.serialNumber}</div>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{assetTypeLabels[asset.type] || asset.type}</td>
                    <td className="px-6 py-4">
                      <span className={statusBadge(asset.status)}>{asset.status?.replace('_', ' ')}</span>
                      <div className="text-xs text-gray-500 mt-1">{asset.condition}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{assetExpiry(asset) ? formatDate(assetExpiry(asset)) : 'No expiry'}</div>
                      <div className="text-xs text-gray-500">{formatCurrency(asset.currentValue || asset.purchaseAmount || 0)}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{asset.location || 'N/A'}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button onClick={() => openModal('view', asset)} className="text-primary-600 hover:text-primary-900" title="View asset">
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        {canManageAssets && (
                          <>
                            <button onClick={() => openModal('edit', asset)} className="text-yellow-600 hover:text-yellow-900" title="Edit asset">
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button onClick={() => openModal('maintenance', asset)} className="text-green-600 hover:text-green-900" title="Add maintenance">
                              <WrenchScrewdriverIcon className="h-5 w-5" />
                            </button>
                            {canArchiveAssets && asset.status !== 'disposed' && (
                              <button onClick={() => archiveAsset(asset)} className="text-red-600 hover:text-red-900" title="Archive asset">
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(modalType === 'create' || modalType === 'edit') && (
        <ModalShell title={modalType === 'create' ? 'Add Asset' : 'Edit Asset'} onClose={closeModal} wide>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Type</label>
                <select {...register('type', { required: 'Type is required' })} className="input-field">
                  <option value="">Select type</option>
                  <option value="license">License</option>
                  <option value="property">Property</option>
                  <option value="equipment">Equipment</option>
                  <option value="document">Document</option>
                </select>
                {errors.type && <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>}
              </div>
              <div>
                <label className="label">Name</label>
                <input {...register('name', { required: 'Asset name is required' })} className="input-field" />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
              </div>
              <div>
                <label className="label">Category</label>
                <input {...register('category')} className="input-field" placeholder="IT, Legal, Office, etc." />
              </div>
              <div>
                <label className="label">Serial / Reference</label>
                <input {...register('serialNumber')} className="input-field" />
              </div>
              <div>
                <label className="label">Purchase Date</label>
                <input {...register('purchaseDate')} type="date" className="input-field" />
              </div>
              <div>
                <label className="label">Purchase Amount</label>
                <input {...register('purchaseAmount')} type="number" step="0.01" className="input-field" />
              </div>
              <div>
                <label className="label">Current Value</label>
                <input {...register('currentValue')} type="number" step="0.01" className="input-field" />
              </div>
              <div>
                <label className="label">Location</label>
                <input {...register('location')} className="input-field" />
              </div>
              <div>
                <label className="label">Condition</label>
                <select {...register('condition')} className="input-field">
                  <option value="excellent">Excellent</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                  <option value="damaged">Damaged</option>
                </select>
              </div>
              <div>
                <label className="label">Status</label>
                <select {...register('status')} className="input-field">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="under_maintenance">Under Maintenance</option>
                  <option value="disposed">Disposed</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
            </div>

            {(assetType === 'license' || assetType === 'document') && (
              <div className="card p-4">
                <h4 className="font-semibold text-gray-900 mb-3">{assetType === 'license' ? 'License Details' : 'Document Details'}</h4>
                {assetType === 'license' ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="label">License Number</label>
                      <input {...register('licenseDetails.licenseNumber')} className="input-field" />
                    </div>
                    <div>
                      <label className="label">Issuing Authority</label>
                      <input {...register('licenseDetails.issuingAuthority')} className="input-field" />
                    </div>
                    <div>
                      <label className="label">Renewal Fee</label>
                      <input {...register('licenseDetails.renewalFee')} type="number" step="0.01" className="input-field" />
                    </div>
                    <div>
                      <label className="label">Issue Date</label>
                      <input {...register('licenseDetails.issueDate')} type="date" className="input-field" />
                    </div>
                    <div>
                      <label className="label">Expiration Date</label>
                      <input {...register('licenseDetails.expirationDate')} type="date" className="input-field" />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="label">Document Number</label>
                      <input {...register('documentDetails.documentNumber')} className="input-field" />
                    </div>
                    <div>
                      <label className="label">Issuing Authority</label>
                      <input {...register('documentDetails.issuingAuthority')} className="input-field" />
                    </div>
                    <div>
                      <label className="label">Valid From</label>
                      <input {...register('documentDetails.validFrom')} type="date" className="input-field" />
                    </div>
                    <div>
                      <label className="label">Valid To</label>
                      <input {...register('documentDetails.validTo')} type="date" className="input-field" />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="label">Description</label>
              <textarea {...register('description')} rows={3} className="input-field" />
            </div>
            <div>
              <label className="label">Notes</label>
              <textarea {...register('notes')} rows={2} className="input-field" />
            </div>
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={isSubmitting} className="btn-primary">
                {isSubmitting ? 'Saving...' : modalType === 'create' ? 'Create Asset' : 'Update Asset'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}

      {modalType === 'view' && selectedAsset && (
        <ModalShell title={selectedAsset.name} onClose={closeModal} wide>
          <div className="space-y-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-gray-500">
                Statement generated {new Date().toLocaleString()}
              </p>
              <div className="flex gap-2 no-print">
                <button type="button" onClick={() => exportAssetCsv(selectedAsset)} className="btn-secondary">Export CSV</button>
                <button type="button" onClick={printAssetStatement} className="btn-secondary">Print</button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div><span className="font-medium text-gray-500">Type:</span> {assetTypeLabels[selectedAsset.type] || selectedAsset.type}</div>
              <div><span className="font-medium text-gray-500">Status:</span> {selectedAsset.status?.replace('_', ' ')}</div>
              <div><span className="font-medium text-gray-500">Condition:</span> {selectedAsset.condition || 'N/A'}</div>
              <div><span className="font-medium text-gray-500">Category:</span> {selectedAsset.category || 'N/A'}</div>
              <div><span className="font-medium text-gray-500">Location:</span> {selectedAsset.location || 'N/A'}</div>
              <div><span className="font-medium text-gray-500">Value:</span> {formatCurrency(selectedAsset.currentValue || selectedAsset.purchaseAmount || 0)}</div>
            </div>
            {selectedAsset.description && <p className="text-sm text-gray-700">{selectedAsset.description}</p>}
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Maintenance History</h4>
              {(selectedAsset.maintenanceHistory || []).length === 0 ? (
                <p className="text-sm text-gray-500">No maintenance records yet.</p>
              ) : (
                <div className="space-y-2">
                  {[...selectedAsset.maintenanceHistory].reverse().map((record) => (
                    <div key={record._id} className="border border-gray-200 rounded-md p-3">
                      <div className="flex justify-between gap-3">
                        <p className="font-medium text-gray-900">{record.description}</p>
                        <p className="font-semibold text-gray-900">{formatCurrency(record.cost)}</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        {formatDate(record.date)} by {record.performedBy || 'N/A'}; next {formatDate(record.nextMaintenanceDate)}
                      </p>
                      <p className="text-xs text-gray-500">
                        Entered by {actorLabel(record.addedBy)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ModalShell>
      )}

      {modalType === 'maintenance' && selectedAsset && (
        <ModalShell title={`Add Maintenance - ${selectedAsset.name}`} onClose={closeModal}>
          <form onSubmit={handleMaintenanceSubmit(onMaintenanceSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Date</label>
                <input {...registerMaintenance('date', { required: 'Date is required' })} type="date" className="input-field" />
                {maintenanceErrors.date && <p className="mt-1 text-sm text-red-600">{maintenanceErrors.date.message}</p>}
              </div>
              <div>
                <label className="label">Cost</label>
                <input {...registerMaintenance('cost')} type="number" step="0.01" className="input-field" />
              </div>
              <div>
                <label className="label">Performed By</label>
                <input {...registerMaintenance('performedBy')} className="input-field" />
              </div>
              <div>
                <label className="label">Next Maintenance Date</label>
                <input {...registerMaintenance('nextMaintenanceDate')} type="date" className="input-field" />
              </div>
            </div>
            <div>
              <label className="label">Description</label>
              <textarea {...registerMaintenance('description', { required: 'Description is required' })} rows={3} className="input-field" />
              {maintenanceErrors.description && <p className="mt-1 text-sm text-red-600">{maintenanceErrors.description.message}</p>}
            </div>
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={closeModal} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={isSavingMaintenance} className="btn-primary">
                {isSavingMaintenance ? 'Saving...' : 'Add Maintenance'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
};

export default Assets;
